/**
 * WebGL2 flow renderer (SPEC §5): GPU-advected particle streamlines over a
 * turbulence raster, drawn on a transparent canvas above the MapLibre map.
 *
 * Two design decisions worth knowing about:
 *
 * 1. **Trails live in grid space, not screen space.** The obvious approach
 *    accumulates trails in a screen-sized framebuffer, but then every pan or
 *    zoom smears the history across the map and has to be cleared. Keeping the
 *    trail buffer aligned to the domain and drawing it through the homography
 *    means trails pan and zoom with the terrain, and survive map movement.
 *
 * 2. **A separate canvas, not a MapLibre custom layer.** The overlay needs full
 *    control of blend state, framebuffer bindings and float textures; sharing
 *    MapLibre's context means restoring all of it every frame and breaking
 *    whenever MapLibre changes its internal GL state.
 */

import type { FieldSlice } from '../physics/types';
import { identity, type Mat3 } from './homography';
import {
  HAZARD_COLOURS,
  GLIDE_BEST_RATIO,
  GLIDE_WORST_RATIO,
  GUST_DISPLAY_MAX,
  SPEEDUP_OCTAVES,
  TI_DISPLAY_MAX,
  TRIM_SPEED_MS,
  hikeFlyRamp,
  rampTexture,
  speedUpRamp,
  streamlineRamp,
  turbulenceRamp,
  watermarkRamp,
} from './colormaps';

const TRAIL_SIZE = 2048;

export type RasterMode =
  | 'none'
  | 'gust'
  | 'turbulence'
  | 'speedup'
  | 'hazard'
  | 'watermark'
  | 'hikefly';

export interface RenderOptions {
  particles: boolean;
  particleCount: number;
  /**
   * Wall-clock seconds for a particle at the field's peak speed to cross the
   * whole domain. Advection is scaled to this rather than to absolute m/s:
   * a real 7 m/s wind crosses a 20 km domain in 48 minutes, which on screen is
   * indistinguishable from stationary. Anchoring to a crossing time keeps the
   * animation legible at every zoom level and domain size.
   *
   * Set it via `crossTimeForWind` so the animation still *reads* faster in a
   * strong wind — a fixed crossing time makes 5 km/h and 50 km/h look
   * identical, which is exactly the wrong impression.
   */
  crossTimeSeconds: number;
  trailPersistence: number;
  raster: RasterMode;
  rasterOpacity: number;
  particleOpacity: number;
  /**
   * Strength of the turbulent perturbation applied to particle motion, as a
   * fraction of local wind speed at full turbulence intensity. 0 renders the
   * mean field only.
   */
  turbulence: number;
  /** Eddy count across the domain; higher means finer-grained churn. */
  eddyScale: number;
  /**
   * Free-stream wind speed, m/s. The speed-up raster is local speed divided by
   * this, so it has to come from the wind input rather than from the field —
   * normalising to the field's own maximum would make the reading change as you
   * pan, which is what the old wind-speed layer did.
   */
  freeStreamSpeed: number;
  /** Altitude the watermark layer floods to, metres above sea level. */
  watermarkAltitude: number;
  /** Fold the current wind into the hike-and-fly score. */
  hikeFlyWind: boolean;
  /** Unit vector the air is travelling towards, in grid axes (east, north). */
  windDir: [number, number];
}

/**
 * Crossing time for a given free-stream wind speed.
 *
 * Real wind speeds span more than an order of magnitude, and playing them back
 * at true relative rate would make a light day look frozen. The compromise is a
 * sub-linear (square-root) mapping about a 10 m/s reference: a 4× change in
 * wind speed shows up as a 2× change in animation rate, which is obvious at a
 * glance without either extreme becoming unwatchable. Clamped so a dead calm
 * still drifts and a gale stays trackable.
 */
export function crossTimeForWind(speedMs: number): number {
  const reference = 10;
  const ratio = Math.sqrt(Math.max(speedMs, 0.3) / reference);
  return Math.min(46, Math.max(6, 13 / ratio));
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  particles: true,
  particleCount: 65536,
  crossTimeSeconds: 14,
  trailPersistence: 0.955,
  raster: 'hazard',
  rasterOpacity: 0.5,
  particleOpacity: 0.9,
  turbulence: 0.85,
  eddyScale: 45,
  freeStreamSpeed: 7,
  watermarkAltitude: 1000,
  hikeFlyWind: false,
  windDir: [1, 0],
};

const QUAD_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos;
  gl_Position = vec4(aPos * 2.0 - 1.0, 0.0, 1.0);
}`;

/** Vertex shader for anything drawn through the map homography. */
const MAP_QUAD_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
uniform mat3 uH;
uniform vec2 uViewport;
out vec2 vUv;
void main() {
  vUv = aPos;
  vec3 p = uH * vec3(aPos, 1.0);
  vec2 px = p.xy / p.z;
  vec2 clip = vec2(px.x / uViewport.x * 2.0 - 1.0, 1.0 - px.y / uViewport.y * 2.0);
  // Multiplying through by w keeps texture interpolation perspective-correct
  // under map pitch.
  gl_Position = vec4(clip * p.z, 0.0, p.z);
}`;

const UPDATE_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;

uniform sampler2D uParticles;
uniform sampler2D uField;      // rgba: u, v, speed, lift
uniform sampler2D uDiag;       // rgba: ti, separation, hazard, gust
uniform float uDt;
uniform float uCrossTime;      // seconds for a peak-speed particle to cross
uniform float uDropRate;
uniform float uSeed;
uniform float uMaxSpeed;
uniform float uTime;
uniform float uTurbGain;
uniform float uEddyScale;

// Hash without sine: stable across drivers, unlike the common sin-based hash.
float hash(vec2 p) {
  uvec2 q = uvec2(ivec2(p * 8192.0)) * uvec2(1597334673u, 3812015801u);
  uint n = (q.x ^ q.y) * 1597334673u;
  return float(n) * (1.0 / 4294967296.0);
}

float hashLattice(vec2 i) {
  uvec2 q = uvec2(ivec2(i)) * uvec2(2246822519u, 3266489917u);
  uint n = (q.x ^ q.y) * 668265263u;
  n = n ^ (n >> 15);
  return float(n) * (1.0 / 4294967296.0);
}

// Value noise with a smoothstep fade — cheap, and smooth enough that the
// perturbation reads as coherent eddies rather than per-pixel static.
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hashLattice(i);
  float b = hashLattice(i + vec2(1.0, 0.0));
  float c = hashLattice(i + vec2(0.0, 1.0));
  float d = hashLattice(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

/**
 * A drifting 2D eddy field. Two decorrelated noise lookups give the components;
 * advancing the sample point in time makes the eddies evolve rather than sit
 * still, so particles wander through them instead of tracing fixed ripples.
 */
vec2 eddy(vec2 p, float t) {
  vec2 q = p * uEddyScale;
  float n1 = vnoise(q + vec2(0.0, t * 0.35));
  float n2 = vnoise(q + vec2(t * 0.31, 11.7));
  // Second octave adds finer structure inside the large eddies.
  n1 += 0.5 * vnoise(q * 2.7 + vec2(t * 0.7, 3.1));
  n2 += 0.5 * vnoise(q * 2.7 + vec2(19.3, t * 0.63));
  return (vec2(n1, n2) / 1.5) * 2.0 - 1.0;
}

void main() {
  vec4 particle = texture(uParticles, vUv);
  vec2 pos = particle.xy;

  vec4 f = texture(uField, pos);
  vec2 vel = f.xy;
  float ti = texture(uDiag, pos).x;

  // Turbulence intensity is sigma_u / U, so a velocity perturbation of
  // ti * |u| is the physically-scaled fluctuation. Without this the mean field
  // alone renders every rotor as glassy-smooth flow, which is exactly the
  // wrong impression for a safety-adjacent tool.
  //
  // smoothstep keeps genuinely smooth air visibly smooth: below TI ~0.03 the
  // perturbation is zero, so laminar and rough are distinguishable at a glance
  // rather than everything jittering a little.
  float turbAmp = uTurbGain * smoothstep(0.03, 0.6, ti);
  vec2 turb = eddy(pos, uTime) * length(vel) * turbAmp;

  // Normalise by the peak speed so the fastest air crosses the unit square in
  // uCrossTime seconds, whatever the domain's real size.
  vec2 step = (vel + turb) * (uDt / (max(uMaxSpeed, 0.1) * uCrossTime));
  vec2 next = pos + step;

  float seed = uSeed + particle.z;
  vec2 rnd = vec2(hash(pos + seed), hash(pos.yx + seed * 1.7));

  // Faster particles are retired sooner, which stops them piling up in
  // convergence zones and keeps the density visually even. Rough air retires
  // them faster still, so turbulent regions show short broken strokes while
  // laminar flow draws long clean lines — the trail *length* carries the
  // signal as much as the wobble does.
  float speedNorm = clamp(f.z / max(uMaxSpeed, 0.1), 0.0, 1.0);
  float drop = uDropRate * (0.35 + 0.65 * speedNorm) * (1.0 + 5.0 * turbAmp);

  bool outside = next.x < 0.0 || next.x > 1.0 || next.y < 0.0 || next.y > 1.0;
  bool stalled = length(step) < 1e-7;
  bool expired = hash(vec2(seed, pos.x + pos.y)) < drop;
  bool respawned = outside || stalled || expired;

  if (respawned) {
    next = rnd;
  }

  // The alpha channel flags a teleport, so the draw pass can break the trail
  // instead of ruling a line straight across the domain.
  outColor = vec4(next, particle.z, respawned ? 0.0 : 1.0);
}`;

/**
 * Trail geometry: one LINE per particle, from where it was last frame to where
 * it is now. Two vertices per particle, even = previous, odd = current.
 *
 * Drawing a single POINT per frame instead — the obvious approach — makes the
 * trail a row of dots whose spacing is the per-frame displacement, and that is
 * frame-rate dependent. At 60 fps the dots land ~2.6 texels apart and merge
 * into a line; at the ~20 fps a phone manages they land ~8 apart and the whole
 * field reads as fine speckle rather than streamlines. Segments are continuous
 * at any frame rate, which is the entire point.
 */
const DRAW_VS = `#version 300 es
precision highp float;
uniform sampler2D uParticles;   // position after this frame's update
uniform sampler2D uPrev;        // position before it
uniform sampler2D uField;
uniform float uTexSize;
uniform float uMaxSpeed;
out float vSpeed;
void main() {
  int id = gl_VertexID;
  int pid = id / 2;
  int w = int(uTexSize);
  ivec2 uv = ivec2(pid % w, pid / w);

  vec4 cur = texelFetch(uParticles, uv, 0);
  vec4 prv = texelFetch(uPrev, uv, 0);

  // cur.w == 0 marks a particle that respawned this frame; collapse the
  // segment onto its new position so no line is drawn across the domain.
  bool teleported = cur.w < 0.5;
  vec2 p = (id % 2 == 0 && !teleported) ? prv.xy : cur.xy;

  vec4 f = texture(uField, cur.xy);
  vSpeed = clamp(f.z / max(uMaxSpeed, 0.1), 0.0, 1.0);
  vSpeed = pow(vSpeed, 0.7);
  gl_PointSize = 1.0;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const DRAW_FS = `#version 300 es
precision highp float;
in float vSpeed;
out vec4 outColor;
uniform sampler2D uRamp;
uniform float uOpacity;
void main() {
  vec3 c = texture(uRamp, vec2(vSpeed, 0.5)).rgb;
  outColor = vec4(c, 1.0) * uOpacity;
}`;

/**
 * Fade the trail buffer towards transparent.
 *
 * The trail texture is 8-bit, and a pure multiply does not reliably reach zero
 * there. At a stored value of 1/255, `1 * 0.955 = 0.955`, and whether that
 * lands back on 1 or drops to 0 depends on whether the driver rounds or
 * truncates when writing the framebuffer. Where it rounds, every pixel a
 * particle has ever touched keeps a permanent residue, and over a few seconds
 * the whole domain fills with stuck dots — which is exactly the fine speckle
 * this produced on iOS while desktop, whose driver truncates, stayed clean.
 *
 * Subtracting a floor larger than one quantisation step guarantees the value
 * strictly decreases every frame on any driver, so trails always die out.
 */
const FADE_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uTrail;
uniform float uPersistence;
void main() {
  vec4 c = texture(uTrail, vUv) * uPersistence;
  outColor = max(vec4(0.0), c - vec4(1.5 / 255.0));
}`;

const SCREEN_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uTex;
uniform float uOpacity;
void main() {
  vec4 c = texture(uTex, vUv);
  outColor = c * uOpacity;
}`;

const RASTER_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uField;   // u, v, speed, lift
uniform sampler2D uDiag;    // ti, separation, hazard, gust
uniform sampler2D uStatic;  // elevation, required glide, fall x, fall y
uniform sampler2D uRamp;
uniform int uMode;          // 1 ti, 3 hazard, 4 gust, 5 speed-up, 6 watermark, 7 hike & fly
uniform float uOpacity;
uniform float uMaxSpeed;
uniform float uTiMax;
uniform float uGustMax;
uniform float uGustKnee;
uniform float uFreeStream;
uniform float uSpeedUpOctaves;
uniform float uWatermarkAlt;
uniform float uGlideBest;
uniform float uGlideWorst;
uniform float uHikeFlyWind;
uniform float uTrimSpeed;
uniform vec2 uWindDir;

void main() {
  vec4 f = texture(uField, vUv);
  vec4 d = texture(uDiag, vUv);
  vec4 st = texture(uStatic, vUv);
  float t;
  float extraAlpha = 1.0;
  float toWhite = 0.0;

  if (uMode == 6) {
    // Everything above the line is simply not this layer's subject.
    float depth = uWatermarkAlt - st.x;
    if (depth <= 0.0) { outColor = vec4(0.0); return; }
    // Shaded by what the flow is doing down there, on the same log-ratio scale
    // the speed-up layer uses, so the two agree about what counts as fast.
    float ratio = f.z / max(uFreeStream, 0.1);
    t = clamp(0.5 + log2(max(ratio, 1e-3)) / (2.0 * uSpeedUpOctaves), 0.0, 1.0);
    // A waterline: without it the flood edge is only as sharp as the colour
    // difference across it, which over gentle ground is no edge at all.
    toWhite = (1.0 - smoothstep(0.0, 30.0, depth)) * 0.55;
  } else if (uMode == 7) {
    float glide = st.y;
    if (uHikeFlyWind > 0.5) {
      // The fall line is the way you would launch; wind travelling the other
      // way is wind blowing up the slope, and is a headwind once you are off.
      float align = -dot(uWindDir, st.zw);

      // Wind costs you glide as well as deciding which way to face. Sink rate
      // is unchanged but ground speed is not, so a headwind eats the ground
      // covered per metre lost — which raises the still-air glide the terrain
      // is asking your wing for. This is a much bigger effect than it looks:
      // half your trim speed on the nose halves your glide.
      float headwind = max(align, 0.0) * uFreeStream;
      glide /= max(1.0 - headwind / uTrimSpeed, 0.15);

      float wf = smoothstep(-0.15, 0.55, align);
      wf *= 1.0 - 0.75 * clamp(d.y, 0.0, 1.0);   // standing in a rotor zone
      // In light wind aspect hardly decides anything, so stop pretending it does.
      wf = mix(1.0, wf, smoothstep(1.0, 4.0, uFreeStream));
      extraAlpha = mix(0.05, 1.0, wf);
    }
    t = clamp(1.0 - log(max(glide, 1e-3) / uGlideBest) / log(uGlideWorst / uGlideBest),
              0.0, 1.0);
  } else if (uMode == 5) {
    // Ratio, so log2 about 1.0: half and double sit equidistant from centre.
    float ratio = f.z / max(uFreeStream, 0.1);
    t = clamp(0.5 + log2(max(ratio, 1e-3)) / (2.0 * uSpeedUpOctaves), 0.0, 1.0);
  } else if (uMode == 3) {
    t = d.z;
  } else if (uMode == 4) {
    // Logarithmic: the useful gust range spans more than a decade, and a
    // linear scale clamps everything rough to the top of the ramp well before
    // the wind gets strong.
    t = clamp(log(1.0 + max(d.w, 0.0) / uGustKnee) / log(1.0 + uGustMax / uGustKnee), 0.0, 1.0);
  } else {
    t = clamp(d.x / max(uTiMax, 0.01), 0.0, 1.0);
  }
  vec4 c = texture(uRamp, vec2(t, 0.5));
  c.rgb = mix(c.rgb, vec3(1.0), toWhite);
  // Alpha rises with the value as well as the colour. Painting the whole ramp
  // at one opacity means calm air hides the terrain while severe air, blended
  // 50/50 with a pale hillshade, comes out pink rather than red. Ramping alpha
  // too keeps smooth air nearly transparent and lets the top of the scale
  // actually read as red.
  float a;
  if (uMode == 3) {
    a = uOpacity;
  } else if (uMode == 6) {
    // A flood reads as a filled region, so alpha stays up across the range
    // rather than fading the calm end out to nothing.
    a = uOpacity * mix(0.62, 1.2, t);
  } else if (uMode == 7) {
    a = uOpacity * smoothstep(0.0, 0.22, t) * mix(0.55, 1.25, t) * extraAlpha;
  } else if (uMode == 5) {
    // Diverging: air carrying on at free-stream speed is the uninteresting
    // case, so the centre of the scale is nearly transparent and both
    // departures fade in.
    a = uOpacity * smoothstep(0.04, 0.30, abs(t - 0.5)) * 1.15;
  } else {
    a = uOpacity * mix(0.45, 1.25, t) * smoothstep(0.02, 0.22, t);
  }
  outColor = vec4(c.rgb, 1.0) * (a * c.a);
}`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`Shader compile failed: ${log}`);
  }
  return sh;
}

function link(gl: WebGL2RenderingContext, vsSrc: string, fsSrc: string): WebGLProgram {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog);
    gl.deleteProgram(prog);
    throw new Error(`Program link failed: ${log}`);
  }
  return prog;
}

type Uniforms = Record<string, WebGLUniformLocation | null>;

function uniforms(gl: WebGL2RenderingContext, prog: WebGLProgram, names: string[]): Uniforms {
  const out: Uniforms = {};
  for (const n of names) out[n] = gl.getUniformLocation(prog, n);
  return out;
}

export class FlowRenderer {
  readonly canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private quadBuf: WebGLBuffer;
  private emptyVao: WebGLVertexArrayObject;

  private progUpdate: WebGLProgram;
  private progDraw: WebGLProgram;
  private progFade: WebGLProgram;
  private progScreen: WebGLProgram;
  private progRaster: WebGLProgram;
  private uUpdate: Uniforms;
  private uDraw: Uniforms;
  private uFade: Uniforms;
  private uScreen: Uniforms;
  private uRaster: Uniforms;

  private particleTex: [WebGLTexture, WebGLTexture];
  private particleFbo: [WebGLFramebuffer, WebGLFramebuffer];
  private particleIndex = 0;
  private texSize = 256;

  private trailTex: [WebGLTexture, WebGLTexture];
  private trailFbo: [WebGLFramebuffer, WebGLFramebuffer];
  private trailIndex = 0;

  private fieldTex: WebGLTexture;
  private diagTex: WebGLTexture;
  private staticTex: WebGLTexture;
  private rampSpeedUp: WebGLTexture;
  private rampStreamline: WebGLTexture;
  private rampTi: WebGLTexture;
  private rampHazard: WebGLTexture;
  private rampWatermark: WebGLTexture;
  private rampHikeFly: WebGLTexture;

  private matrix: Mat3 = identity();
  private maxSpeed = 15;
  private elapsed = 0;
  private hasField = false;
  private hasStatic = false;
  private opts: RenderOptions = { ...DEFAULT_RENDER_OPTIONS };

  /** True when the GPU cannot render to float textures and particles are off. */
  readonly floatUnsupported: boolean;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is required for the flow overlay');
    this.gl = gl;

    // Particle positions need a float render target; without it the particle
    // layer is disabled and the arrow layer carries the field instead.
    this.floatUnsupported = !gl.getExtension('EXT_color_buffer_float');

    this.quadBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
      gl.STATIC_DRAW
    );
    this.emptyVao = gl.createVertexArray()!;

    this.progUpdate = link(gl, QUAD_VS, UPDATE_FS);
    this.progDraw = link(gl, DRAW_VS, DRAW_FS);
    this.progFade = link(gl, QUAD_VS, FADE_FS);
    this.progScreen = link(gl, MAP_QUAD_VS, SCREEN_FS);
    this.progRaster = link(gl, MAP_QUAD_VS, RASTER_FS);

    this.uUpdate = uniforms(gl, this.progUpdate, [
      'uParticles', 'uField', 'uDiag', 'uDt', 'uCrossTime', 'uDropRate', 'uSeed',
      'uMaxSpeed', 'uTime', 'uTurbGain', 'uEddyScale',
    ]);
    this.uDraw = uniforms(gl, this.progDraw, [
      'uParticles', 'uPrev', 'uField', 'uTexSize', 'uMaxSpeed', 'uRamp', 'uOpacity',
    ]);
    this.uFade = uniforms(gl, this.progFade, ['uTrail', 'uPersistence']);
    this.uScreen = uniforms(gl, this.progScreen, ['uTex', 'uOpacity', 'uH', 'uViewport']);
    this.uRaster = uniforms(gl, this.progRaster, [
      'uField', 'uDiag', 'uStatic', 'uRamp', 'uMode', 'uOpacity', 'uH', 'uViewport',
      'uMaxSpeed', 'uTiMax', 'uGustMax', 'uGustKnee', 'uFreeStream', 'uSpeedUpOctaves',
      'uWatermarkAlt', 'uGlideBest', 'uGlideWorst', 'uHikeFlyWind', 'uWindDir',
      'uTrimSpeed',
    ]);

    this.fieldTex = this.makeFieldTexture();
    this.diagTex = this.makeFieldTexture();
    this.staticTex = this.makeFieldTexture();
    this.rampSpeedUp = this.makeRamp(rampTexture(speedUpRamp));
    this.rampStreamline = this.makeRamp(rampTexture(streamlineRamp));
    this.rampTi = this.makeRamp(rampTexture(turbulenceRamp));
    this.rampHazard = this.makeRamp(this.hazardRamp());
    this.rampWatermark = this.makeRamp(rampTexture(watermarkRamp));
    this.rampHikeFly = this.makeRamp(rampTexture(hikeFlyRamp));

    const [pa, pb] = [this.allocParticleTex(), this.allocParticleTex()];
    this.particleTex = [pa, pb];
    this.particleFbo = [this.makeFbo(pa), this.makeFbo(pb)];

    const [ta, tb] = [this.allocTrailTex(), this.allocTrailTex()];
    this.trailTex = [ta, tb];
    this.trailFbo = [this.makeFbo(ta), this.makeFbo(tb)];

    this.setParticleCount(this.opts.particleCount);
  }

  private makeTexture(
    w: number,
    h: number,
    internal: number,
    format: number,
    type: number,
    data: ArrayBufferView | null
  ): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /**
   * Field textures are sampled with bilinear filtering so the flow and the
   * raster are smooth between grid cells rather than showing hard cell blocks.
   *
   * **RGBA16F, not RGBA32F, and that matters.** Linear filtering of a 32-bit
   * float texture requires `OES_texture_float_linear`, which iOS does not
   * provide — so on iPhone the previous code silently fell back to NEAREST and
   * every grid cell became a visible square. Desktop, where the extension
   * exists, looked fine, which is why this read as a mobile-only rendering
   * fault. Half-float textures are filterable in core WebGL2 with no extension
   * at all, so LINEAR now works everywhere.
   *
   * Half precision is ample here: the largest value stored is a wind speed of a
   * few tens of m/s, and half-float carries ~3 decimal digits. Particle
   * positions still use 32F — those are normalised coordinates where the
   * quantisation would show.
   */
  private makeFieldTexture(): WebGLTexture {
    return this.makeTexture(1, 1, this.gl.RGBA16F, this.gl.RGBA, this.gl.FLOAT, new Float32Array(4));
  }

  private makeRamp(data: Uint8Array): WebGLTexture {
    const gl = this.gl;
    return this.makeTexture(256, 1, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, data);
  }

  private hazardRamp(): Uint8Array {
    const out = new Uint8Array(256 * 4);
    for (let i = 0; i < 256; i++) {
      const cls = Math.min(4, Math.round((i / 255) * 4));
      const [r, g, b] = HAZARD_COLOURS[cls];
      out[i * 4] = Math.round(r * 255);
      out[i * 4 + 1] = Math.round(g * 255);
      out[i * 4 + 2] = Math.round(b * 255);
      out[i * 4 + 3] = 255;
    }
    return out;
  }

  private allocParticleTex(): WebGLTexture {
    const gl = this.gl;
    const tex = this.makeTexture(
      this.texSize, this.texSize, gl.RGBA32F, gl.RGBA, gl.FLOAT, null
    );
    // Nearest: each texel is one particle, interpolating between them is wrong.
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    return tex;
  }

  private allocTrailTex(): WebGLTexture {
    const gl = this.gl;
    return this.makeTexture(
      TRAIL_SIZE, TRAIL_SIZE, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, null
    );
  }

  private makeFbo(tex: WebGLTexture): WebGLFramebuffer {
    const gl = this.gl;
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fbo;
  }

  setOptions(opts: Partial<RenderOptions>): void {
    const prevCount = this.opts.particleCount;
    this.opts = { ...this.opts, ...opts };
    if (this.opts.particleCount !== prevCount) {
      this.setParticleCount(this.opts.particleCount);
    }
  }

  getOptions(): RenderOptions {
    return { ...this.opts };
  }

  private setParticleCount(count: number): void {
    const gl = this.gl;
    const size = Math.max(16, Math.ceil(Math.sqrt(Math.max(1, count))));
    this.texSize = size;

    const seed = new Float32Array(size * size * 4);
    for (let i = 0; i < size * size; i++) {
      seed[i * 4] = Math.random();
      seed[i * 4 + 1] = Math.random();
      seed[i * 4 + 2] = Math.random();
      seed[i * 4 + 3] = 1;
    }

    for (let i = 0; i < 2; i++) {
      gl.deleteTexture(this.particleTex[i]);
      gl.deleteFramebuffer(this.particleFbo[i]);
      const tex = this.allocParticleTex();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, size, size, 0, gl.RGBA, gl.FLOAT, seed);
      this.particleTex[i] = tex;
      this.particleFbo[i] = this.makeFbo(tex);
    }
    this.clearTrails();
  }

  /** Upload a solved field slice. Textures are grid-sized, so this is cheap. */
  setField(slice: FieldSlice): void {
    const gl = this.gl;
    const { width: w, height: h } = slice;
    const field = new Float32Array(w * h * 4);
    const diag = new Float32Array(w * h * 4);

    for (let i = 0; i < w * h; i++) {
      field[i * 4] = slice.u[i];
      field[i * 4 + 1] = slice.v[i];
      field[i * 4 + 2] = slice.speed[i];
      field[i * 4 + 3] = slice.lift[i];
      diag[i * 4] = slice.ti[i];
      diag[i * 4 + 1] = slice.separation[i];
      // Hazard is 0–4; store normalised so the ramp lookup is a plain fetch.
      diag[i * 4 + 2] = slice.hazard[i] / 4;
      diag[i * 4 + 3] = slice.gust[i];
    }

    gl.bindTexture(gl.TEXTURE_2D, this.fieldTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.FLOAT, field);
    gl.bindTexture(gl.TEXTURE_2D, this.diagTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.FLOAT, diag);

    this.maxSpeed = Math.max(1, slice.maxSpeed);
    this.hasField = true;
    this.clearTrails();
  }

  /**
   * Upload the wind-independent terrain fields.
   *
   * Called once per elevation grid rather than per solve, and again when the
   * glide requirement — which is computed lazily — arrives for a grid already
   * loaded. `requiredGlide` may be null before then; the layer that reads it
   * simply has nothing to draw until it is not.
   *
   * Half-float holds elevation to about 2 m at alpine altitudes, well inside
   * the step the altitude slider moves in.
   */
  setTerrainLayers(layers: {
    width: number;
    height: number;
    elevation: Float32Array;
    requiredGlide: Float32Array | null;
    fallX: Float32Array;
    fallY: Float32Array;
  }): void {
    const gl = this.gl;
    const { width: w, height: h } = layers;
    const data = new Float32Array(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      data[i * 4] = layers.elevation[i];
      data[i * 4 + 1] = layers.requiredGlide ? layers.requiredGlide[i] : 1e4;
      data[i * 4 + 2] = layers.fallX[i];
      data[i * 4 + 3] = layers.fallY[i];
    }
    gl.bindTexture(gl.TEXTURE_2D, this.staticTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.FLOAT, data);
    this.hasStatic = true;
  }

  setMatrix(m: Mat3): void {
    this.matrix = m;
  }

  clearTrails(): void {
    const gl = this.gl;
    for (const fbo of this.trailFbo) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  resize(width: number, height: number, dpr: number): void {
    const w = Math.max(1, Math.round(width * dpr));
    const h = Math.max(1, Math.round(height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }

  private bindQuad(prog: WebGLProgram): void {
    const gl = this.gl;
    gl.useProgram(prog);
    gl.bindVertexArray(this.emptyVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  }

  private bindTex(unit: number, tex: WebGLTexture, loc: WebGLUniformLocation | null): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    if (loc) gl.uniform1i(loc, unit);
  }

  /** Advance the particle simulation and composite one frame. */
  render(dtSeconds: number): void {
    const gl = this.gl;
    const dt = Math.min(0.05, Math.max(0.001, dtSeconds));
    this.elapsed += dt;

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    // The terrain-only layers can draw before the first solve has landed.
    if (!this.hasField && !this.hasStatic) return;

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    if (this.opts.raster !== 'none') this.drawRaster();

    if (this.opts.particles && this.hasField && !this.floatUnsupported) {
      this.updateParticles(dt);
      this.drawTrails();
      this.compositeTrails();
    }

    gl.bindVertexArray(null);
  }

  private drawRaster(): void {
    const gl = this.gl;
    const modes: Record<RasterMode, number> = {
      none: 0, turbulence: 1, hazard: 3, gust: 4, speedup: 5, watermark: 6, hikefly: 7,
    };
    const mode = modes[this.opts.raster];
    // Gust and TI share the ramp: same meaning, different normalisation.
    const ramp =
      mode === 5
        ? this.rampSpeedUp
        : mode === 3
          ? this.rampHazard
          : mode === 6
            ? this.rampWatermark
            : mode === 7
              ? this.rampHikeFly
              : this.rampTi;

    // Both terrain layers need the static texture; the watermark also needs a
    // solved field to shade with, so it waits for one.
    if ((mode === 6 || mode === 7) && !this.hasStatic) return;
    if (mode !== 7 && !this.hasField) return;

    this.bindQuad(this.progRaster);
    this.bindTex(0, this.fieldTex, this.uRaster.uField);
    this.bindTex(1, this.diagTex, this.uRaster.uDiag);
    this.bindTex(2, ramp, this.uRaster.uRamp);
    this.bindTex(3, this.staticTex, this.uRaster.uStatic);
    gl.uniform1i(this.uRaster.uMode, mode);
    gl.uniform1f(this.uRaster.uOpacity, this.opts.rasterOpacity);
    gl.uniform1f(this.uRaster.uMaxSpeed, this.maxSpeed);
    gl.uniform1f(this.uRaster.uTiMax, TI_DISPLAY_MAX);
    gl.uniform1f(this.uRaster.uGustMax, GUST_DISPLAY_MAX);
    gl.uniform1f(this.uRaster.uGustKnee, 0.8);
    gl.uniform1f(this.uRaster.uFreeStream, this.opts.freeStreamSpeed);
    gl.uniform1f(this.uRaster.uSpeedUpOctaves, SPEEDUP_OCTAVES);
    gl.uniform1f(this.uRaster.uWatermarkAlt, this.opts.watermarkAltitude);
    gl.uniform1f(this.uRaster.uGlideBest, GLIDE_BEST_RATIO);
    gl.uniform1f(this.uRaster.uGlideWorst, GLIDE_WORST_RATIO);
    gl.uniform1f(this.uRaster.uHikeFlyWind, this.opts.hikeFlyWind ? 1 : 0);
    gl.uniform1f(this.uRaster.uTrimSpeed, TRIM_SPEED_MS);
    gl.uniform2f(this.uRaster.uWindDir, this.opts.windDir[0], this.opts.windDir[1]);
    gl.uniformMatrix3fv(this.uRaster.uH, false, this.matrix);
    gl.uniform2f(this.uRaster.uViewport, this.canvas.width, this.canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private updateParticles(dt: number): void {
    const gl = this.gl;
    const next = 1 - this.particleIndex;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.particleFbo[next]);
    gl.viewport(0, 0, this.texSize, this.texSize);
    gl.disable(gl.BLEND);

    this.bindQuad(this.progUpdate);
    this.bindTex(0, this.particleTex[this.particleIndex], this.uUpdate.uParticles);
    this.bindTex(1, this.fieldTex, this.uUpdate.uField);
    this.bindTex(2, this.diagTex, this.uUpdate.uDiag);
    gl.uniform1f(this.uUpdate.uDt, dt);
    gl.uniform1f(this.uUpdate.uTime, this.elapsed);
    gl.uniform1f(this.uUpdate.uTurbGain, this.opts.turbulence);
    gl.uniform1f(this.uUpdate.uEddyScale, this.opts.eddyScale);
    gl.uniform1f(this.uUpdate.uCrossTime, this.opts.crossTimeSeconds);
    gl.uniform1f(this.uUpdate.uDropRate, 0.004);
    gl.uniform1f(this.uUpdate.uSeed, Math.random());
    gl.uniform1f(this.uUpdate.uMaxSpeed, this.maxSpeed);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    this.particleIndex = next;
    gl.enable(gl.BLEND);
  }

  private drawTrails(): void {
    const gl = this.gl;
    const next = 1 - this.trailIndex;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.trailFbo[next]);
    gl.viewport(0, 0, TRAIL_SIZE, TRAIL_SIZE);

    // Fade the previous frame rather than clearing, which is what produces
    // the trail behind each particle.
    gl.disable(gl.BLEND);
    this.bindQuad(this.progFade);
    this.bindTex(0, this.trailTex[this.trailIndex], this.uFade.uTrail);
    gl.uniform1f(this.uFade.uPersistence, this.opts.trailPersistence);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.progDraw);
    gl.bindVertexArray(this.emptyVao);
    this.bindTex(0, this.particleTex[this.particleIndex], this.uDraw.uParticles);
    this.bindTex(1, this.fieldTex, this.uDraw.uField);
    this.bindTex(2, this.rampStreamline, this.uDraw.uRamp);
    // updateParticles has already flipped the index, so the other buffer still
    // holds last frame's positions — the other end of each segment.
    this.bindTex(3, this.particleTex[1 - this.particleIndex], this.uDraw.uPrev);
    gl.uniform1f(this.uDraw.uTexSize, this.texSize);
    gl.uniform1f(this.uDraw.uMaxSpeed, this.maxSpeed);
    gl.uniform1f(this.uDraw.uOpacity, this.opts.particleOpacity);
    gl.drawArrays(gl.LINES, 0, this.texSize * this.texSize * 2);

    this.trailIndex = next;
  }

  private compositeTrails(): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    this.bindQuad(this.progScreen);
    this.bindTex(0, this.trailTex[this.trailIndex], this.uScreen.uTex);
    gl.uniform1f(this.uScreen.uOpacity, 1);
    gl.uniformMatrix3fv(this.uScreen.uH, false, this.matrix);
    gl.uniform2f(this.uScreen.uViewport, this.canvas.width, this.canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  dispose(): void {
    const gl = this.gl;
    for (const t of [...this.particleTex, ...this.trailTex, this.fieldTex, this.diagTex,
      this.rampSpeedUp, this.rampStreamline, this.rampTi, this.rampHazard]) gl.deleteTexture(t);
    for (const f of [...this.particleFbo, ...this.trailFbo]) gl.deleteFramebuffer(f);
    for (const p of [this.progUpdate, this.progDraw, this.progFade, this.progScreen,
      this.progRaster]) gl.deleteProgram(p);
    gl.deleteBuffer(this.quadBuf);
    gl.deleteVertexArray(this.emptyVao);
  }
}
